import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Request from './AxiosInstance';
import { toast } from 'react-toastify';
import Alert from '@mui/material/Alert';

const TelehealthConsentForm = ({ open, onClose, onSubmitSuccess, procedureId, formContentHtml, expectedFullName }) => {
  const [signature, setSignature] = useState('');
  const [signatureError, setSignatureError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
        setSignature('');
        setSignatureError('');
        setSubmitting(false);
    }
  }, [open]);

  const validateSignature = () => {
    if (!signature.trim()) {
        setSignatureError('Signature is required.');
        return false;
    }
    if (signature.trim().toLowerCase() !== expectedFullName.toLowerCase()) {
        setSignatureError(`Signature must match your full name: ${expectedFullName}`);
        return false;
    }
    setSignatureError('');
    return true;
  };

  const handleSubmit = async () => {
    if (validateSignature() && !submitting) {
      try {
        setSubmitting(true);
        await Request.post('/api/capsule/consent', { procedureId, signature: signature.trim() });
        if (onSubmitSuccess) onSubmitSuccess();
        onClose();
        toast.success('Consent submitted successfully!');
      } catch (error) {
        console.error('Error submitting consent form:', error);
        toast.error('Error submitting consent form. Please try again.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', color: '#1976d2' }}>
        At-Home Capsule Endoscopy Consent Form
      </DialogTitle>
      <Divider />
      <DialogContent>
        {formContentHtml ? (
            <Box sx={{ p: 2 }} dangerouslySetInnerHTML={{ __html: formContentHtml }} />
        ) : (
            <Typography>Loading form content...</Typography>
        )}

        <Box sx={{ mt: 4, p: 2 }}>
            <Typography variant="body1" gutterBottom>
                Please type in your full name (<b>{expectedFullName}</b>) as signature:
            </Typography>
            <TextField
                fullWidth
                variant="outlined"
                label="Full Name Signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                onBlur={validateSignature}
                error={!!signatureError}
                helperText={signatureError || "Please make sure the name matches your full name above."}
                required
                margin="normal"
            />
            <Alert severity="info" sx={{ mt: 2 }}>
                 By typing your name above and clicking "Agree and Sign", you confirm that you have read, understood, and agree to the terms outlined in this consent form.
            </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={!signature || !!signatureError || submitting}
          variant="contained"
          size="large"
        >
          {submitting ? 'Submitting...' : 'Agree and Sign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TelehealthConsentForm; 